import { useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Slider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import { curingLabel, useCreateCuringObservation } from '../../api/curing';
import type { CuringConfidence, CuringMethod } from '../../types/curing';

interface CuringObservationDialogProps {
    open: boolean;
    onClose: () => void;
    projectId: string;
    /** Project centre, used as the starting point for the location. */
    defaultLat?: number | null;
    defaultLon?: number | null;
}

const METHODS: Array<{ value: CuringMethod; label: string }> = [
    { value: 'visual', label: 'Looked at it' },
    { value: 'levy_rod', label: 'Levy rod' },
    { value: 'destructive', label: 'Cut and weighed' },
    { value: 'photo', label: 'From a photo' },
];

const CONFIDENCES: Array<{ value: CuringConfidence; label: string }> = [
    { value: 'high', label: 'Confident' },
    { value: 'medium', label: 'Fairly sure' },
    { value: 'low', label: 'Rough guess' },
];

/**
 * Record what someone saw in the paddock. The observation corrects the
 * satellite reading nearby, so it is worth capturing even a rough one.
 */
const CuringObservationDialog: React.FC<CuringObservationDialogProps> = ({
    open,
    onClose,
    projectId,
    defaultLat,
    defaultLon,
}) => {
    const today = new Date().toISOString().slice(0, 10);
    const [curing, setCuring] = useState(80);
    const [lat, setLat] = useState(defaultLat != null ? String(defaultLat) : '');
    const [lon, setLon] = useState(defaultLon != null ? String(defaultLon) : '');
    const [observedOn, setObservedOn] = useState(today);
    const [method, setMethod] = useState<CuringMethod>('visual');
    const [confidence, setConfidence] = useState<CuringConfidence>('medium');
    const [note, setNote] = useState('');
    const [error, setError] = useState<string | null>(null);

    const create = useCreateCuringObservation(projectId);

    const latNum = Number(lat);
    const lonNum = Number(lon);
    const locationValid =
        lat !== '' && lon !== '' &&
        Number.isFinite(latNum) && Number.isFinite(lonNum) &&
        latNum >= -90 && latNum <= 90 && lonNum >= -180 && lonNum <= 180;
    const dateValid = observedOn !== '' && observedOn <= today;

    const handleSave = async () => {
        setError(null);
        try {
            await create.mutateAsync({
                lat: latNum,
                lon: lonNum,
                curing_pct: curing,
                observed_on: observedOn,
                method,
                confidence,
                note: note.trim() || undefined,
            });
            onClose();
        } catch {
            setError('That observation could not be saved. Please try again.');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Record a curing observation</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    What the grass was actually doing, and where. This nudges the
                    satellite reading nearby towards what you saw, for about a
                    fortnight and a few kilometres around the spot.
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Typography variant="caption" color="text.secondary">
                    Curing: <strong>{curing}%</strong> ({curingLabel(curing)})
                </Typography>
                <Slider
                    value={curing}
                    onChange={(_, v) => setCuring(v as number)}
                    min={0}
                    max={100}
                    step={5}
                    marks={[
                        { value: 0, label: 'Green' },
                        { value: 50, label: '50%' },
                        { value: 100, label: 'Fully cured' },
                    ]}
                    sx={{ mb: 2 }}
                />

                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        label="Latitude" value={lat} onChange={(e) => setLat(e.target.value)}
                        size="small" fullWidth
                        error={lat !== '' && !locationValid}
                    />
                    <TextField
                        label="Longitude" value={lon} onChange={(e) => setLon(e.target.value)}
                        size="small" fullWidth
                        error={lon !== '' && !locationValid}
                    />
                </Stack>

                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                    <TextField
                        label="Date seen" type="date" value={observedOn}
                        onChange={(e) => setObservedOn(e.target.value)}
                        size="small" fullWidth
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: today }}
                        error={!dateValid}
                        helperText={!dateValid ? 'Cannot be in the future' : ' '}
                    />
                    <TextField
                        select label="How" value={method}
                        onChange={(e) => setMethod(e.target.value as CuringMethod)}
                        size="small" fullWidth
                    >
                        {METHODS.map((m) => (
                            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select label="How sure" value={confidence}
                        onChange={(e) => setConfidence(e.target.value as CuringConfidence)}
                        size="small" fullWidth
                    >
                        {CONFIDENCES.map((c) => (
                            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                        ))}
                    </TextField>
                </Stack>

                <TextField
                    label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)}
                    size="small" fullWidth multiline rows={2}
                    placeholder="e.g. green pick coming through after 30 mm last week"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!locationValid || !dateValid || create.isPending}
                >
                    {create.isPending ? 'Saving…' : 'Save observation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CuringObservationDialog;
